<template>
  <main role="main" class="py-4">
    <b-container class="container-x" style="max-width: 800px;">
      <!-- Title Section -->
      <div class="d-flex flex-row align-items-center justify-content-between mb-4 border-bottom pb-2">
        <h4 class="m-0 text-dark font-weight-bold">{{ $t("configPasswordPolicy") }}</h4>
      </div>

      <!-- Config Card -->
      <b-card class="shadow-sm border">
        <div v-if="isLoading" class="py-5 text-center">
          <b-spinner variant="primary"></b-spinner>
          <div class="text-muted mt-2 small">{{ $t("loadingData") }}</div>
        </div>

        <b-form v-if="!isLoading && currentPasswordPolicy" @keydown="formKeyDown" @submit.prevent="submitForm">
          
          <!-- Section 1: Complexity & Rules -->
          <div class="mb-4">
            <h6 class="text-primary font-weight-bold mb-3">{{ $t("passwordFormatConfig") }}</h6>
            
            <b-form-row>
              <b-form-group class="col-lg-6" label-for="input-min-password-length" label-class="font-weight-bold text-secondary small">
                <template v-slot:label>{{ $t("passwordMinLength") }} <span class="text-danger">*</span></template>
                <b-form-input id="input-min-password-length" type="number" required min="6" max="16"
                              v-model="minPasswordLength" name="minPasswordLength"/>
              </b-form-group>
              <b-form-group class="col-lg-6" label-for="input-max-password-length" label-class="font-weight-bold text-secondary small">
                <template v-slot:label>{{ $t("passwordMaxLength") }} <span class="text-danger">*</span></template>
                <b-form-input id="input-max-password-length" type="number" required min="6" max="32"
                              v-model="maxPasswordLength" name="maxPasswordLength"></b-form-input>
              </b-form-group>
            </b-form-row>

            <b-form-group label-for="input-password-pattern" :label="$t('passwordPattern')" label-class="font-weight-bold text-secondary small">
              <b-form-input id="input-password-pattern" type="text" v-model="passwordPattern" name="passwordPattern" maxlength="255"/>
            </b-form-group>

            <b-form-group label-for="input-password-description" :label="$t('passwordDescription')" label-class="font-weight-bold text-secondary small">
              <b-form-input id="input-password-description" type="text" maxlength="255"
                            v-model="passwordDescription" name="passwordDescription"></b-form-input>
            </b-form-group>

            <b-form-group label-for="input-password-validation-failed-message" :label="$t('passwordValidationFailedMessage')" label-class="font-weight-bold text-secondary small">
              <b-form-input id="input-password-validation-failed-message" type="text" maxlength="255"
                            v-model="passwordValidationFailedMessage" name="passwordValidationFailedMessage" ></b-form-input>
            </b-form-group>
          </div>

          <!-- Section 2: Security & Lockout -->
          <div class="mb-4 border-top pt-3">
            <h6 class="text-primary font-weight-bold mb-3">{{ $t("securityLockoutConfig") }}</h6>

            <b-form-row>
              <b-form-group class="col-lg-6" label-for="input-password-validity-period" label-class="font-weight-bold text-secondary small">
                <template v-slot:label>{{ $t("passwordValidityPeriod") }} <span class="text-danger">*</span></template>
                <b-form-input id="input-password-validity-period" type="number" required min="1" max="3650"
                              v-model="passwordValidityPeriod" name="passwordValidityPeriod"></b-form-input>
              </b-form-group>
              <b-form-group class="col-lg-6" label-for="input-wrong-password-attempt" label-class="font-weight-bold text-secondary small">
                <template v-slot:label>{{ $t("wrongPasswordAttempt") }} <span class="text-danger">*</span></template>
                <b-form-input id="input-wrong-password-attempt" type="number" required min="1" max="100"
                              v-model="wrongPasswordAttempt" name="wrongPasswordAttempt"></b-form-input>
              </b-form-group>
            </b-form-row>

            <b-form-row>
              <b-form-group class="col-lg-6" label-for="radio-locked-out" :label="$t('passwordLockedOut')" label-class="font-weight-bold text-secondary small">
                <b-form-radio-group id="radio-locked-out" :options="lockedOutOptions"
                                    v-model="lockedOut" name="lockedOut" class="pt-2"></b-form-radio-group>
              </b-form-group>
              <b-form-group class="col-lg-6" label-for="input-locked-out-duration" label-class="font-weight-bold text-secondary small">
                <template v-slot:label>{{ $t("passwordLockedOutDuration") }} <span class="text-danger">*</span></template>
                <b-form-input id="input-locked-out-duration" type="number" required min="1" max="1440"
                              v-model="lockedOutDuration" name="lockedOutDuration"></b-form-input>
              </b-form-group>
            </b-form-row>

            <b-form-group label-for="radio-must-change-default-password" :label="$t('mustChangeDefaultPassword')" label-class="font-weight-bold text-secondary small">
              <b-form-radio-group id="radio-must-change-default-password" :options="mustChangeDefaultPasswordOptions"
                                  v-model="mustChangeDefaultPassword" name="mustChangeDefaultPassword" class="pt-2"></b-form-radio-group>
            </b-form-group>
          </div>

          <!-- Footer Area -->
          <div class="d-flex flex-column flex-sm-row align-items-center justify-content-between border-top pt-3 mt-4">
            <div class="text-muted small mb-3 mb-sm-0">
              <span v-if="formErrorMsg" class="text-danger font-weight-bold">{{ formErrorMsg }}</span>
            </div>
            
            <b-button type="submit" variant="primary" :disabled="isSubmitting" style="min-width: 140px;" @click="beforeSubmit">
              <b-spinner v-if="isSubmitting" small class="mr-1"></b-spinner>
              {{ $t("save") }}
            </b-button>
          </div>
        </b-form>
      </b-card>
    </b-container>
  </main>
</template>

<style lang="scss" scoped>
.container-x {
  max-width: 800px;
}
.card {
  border-radius: 8px;
  background-color: #f4f9ff;
}
.font-weight-bold {
  font-weight: 600 !important;
}
</style>

<script lang="ts">
  import {Component, Emit, Prop} from 'vue-property-decorator';
  import _ from "lodash";
  import BaseComponent from "@/components/BaseComponent";
  import * as passwordPolicyService from "@/service/passwordPolicyService";

  @Component
  export default class PasswordPolicy extends BaseComponent {

    isLoading = true;

    currentPasswordPolicy = null;

    minPasswordLength = 0;
    maxPasswordLength = 0;
    passwordPattern = "";
    passwordDescription = "";
    passwordValidationFailedMessage = "";
    passwordValidityPeriod = 0;
    wrongPasswordAttempt = 0;

    lockedOutOptions = [
      { value: true, text: this.$t("yes").toString() },
      { value: false, text: this.$t("no").toString() },
    ];
    lockedOut = true;

    lockedOutDuration = 0;

    mustChangeDefaultPasswordOptions = [
      { value: true, text: this.$t("yes").toString() },
      { value: false, text: this.$t("no").toString() },
    ];
    mustChangeDefaultPassword = true;

    formErrorMsg = "";
    isSubmitting = false;

    // Emit decorators only
    @Emit("submitted") __submitted() {}


    mounted() {
      this.fetchPasswordPolicy();
    }

    async fetchPasswordPolicy() {
      try {        
        this.isLoading = true;
        this.currentPasswordPolicy = await passwordPolicyService.get();
        Object.assign(this, this.currentPasswordPolicy);

      } catch (e) {
        console.error(e);

      } finally {
        this.isLoading = false;
      }
    }

    formKeyDown(event) {
      return event.key !== 'Enter';
    }

    beforeSubmit() {
      const trimFields = ["passwordPattern", "passwordDescription", "passwordValidationFailedMessage"];
      for (const field of trimFields) {
        this[field] = this[field].trim();
      }
    }

    async submitForm() {
      // check regexp
      try {
        new RegExp(this.passwordPattern);
      } catch (e) {
        this.formErrorMsg = this.$t("invalidPasswordPattern").toString();
        return;
      }

      try {
        this.isSubmitting = true;

        const body = {
          minPasswordLength: +this.minPasswordLength,
          maxPasswordLength: +this.maxPasswordLength,
          passwordPattern: this.passwordPattern,
          passwordDescription: this.passwordDescription,
          passwordValidationFailedMessage: this.passwordValidationFailedMessage,
          passwordValidityPeriod: +this.passwordValidityPeriod,
          wrongPasswordAttempt: +this.wrongPasswordAttempt,
          lockedOut: this.lockedOut,
          lockedOutDuration: +this.lockedOutDuration,
          mustChangeDefaultPassword: this.mustChangeDefaultPassword
        };
        await passwordPolicyService.set(body);
        this.toastSuccessDelay(this.$t("saveSettingsSuccessful").toString(), null, 0);
        this.fetchPasswordPolicy();

      } catch (e) {
        this.toastError(e);

      } finally {
        this.isSubmitting = false;
      }
    }

  }
</script>
